import { IconButton } from "@/components/Button/Button";
import Input from "@/components/Forms/Input";
import { DeleteItem } from "@/components/Icons";
import LanguageSelector from "@/components/LanguageSelector/LanguageSelector";
import { useTranslations } from "@/contexts/Translation";
import { EmailLang, ValidationError } from "@/types";
import { forwardRef, useState } from "react";
import styles from "./Invites.module.css";

type EmailLangInput = {
  email: string;
  lang: string;
  emailId?: string;
  onChange: (value: EmailLang) => void;
  deleteEmail?: (emailId: string) => void;
  animateIn?: boolean;
};
export type Ref = HTMLInputElement;

const EmailLangInput = forwardRef<Ref, EmailLangInput>(({ email, lang, onChange, deleteEmail, emailId, animateIn = false }, ref) => {
  const t = useTranslations("invites");
  const [error, setError] = useState<ValidationError | null>(null);
  const [emailInput, setEmailInput] = useState<string>(email);
  const [langInput, setLangInput] = useState<string>(lang);
  const [entering, setEntering] = useState<boolean>(animateIn);

  const isValidEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleBlur = () => {
    if (!emailInput) {
      return;
    }
    if (!isValidEmail(emailInput)) {
      setError({ name: `email-${emailId}`, message: t("invalidEmail") });
    } else {
      setError(null);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // The parent hears every keystroke, so the next empty row appears while typing and not on blur.
    setError(null);
    setEmailInput(e.currentTarget.value);
    onChange({ email: e.currentTarget.value, lang: langInput, emailId });
  };

  const handleLangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLangInput(e.currentTarget.value);
    onChange({ email: emailInput, lang: e.currentTarget.value, emailId });
  };

  return (
    <div
      className={[styles.emailRow, entering && styles.emailRowEnter].filter(Boolean).join(" ")}
      data-testid="invite-email-row"
      onAnimationEnd={() => setEntering(false)}
    >
      <div className={styles.emailGrid}>
        <div>
          <Input
            className={styles.emailInputContainer}
            ref={ref}
            error={error}
            fieldClassName={styles.emailField}
            type='email'
            name={`invite-${emailId}`}
            handleChange={handleEmailChange}
            handleBlur={handleBlur}
            label={t("emailLabel")}
            value={emailInput}
          />
        </div>
        <div>
          <LanguageSelector
            label={t("languageLabel")}
            handleChange={handleLangChange}
            name={`lang-${emailId}`} // Updated name to be unique per index
            value={langInput}
          />
        </div>
        <div>
          {deleteEmail && emailId && email.length > 0 && (
            <IconButton onClick={() => deleteEmail(emailId)}>
              <DeleteItem />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
});

export default EmailLangInput;
